import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import {
	formatRate,
	IAddEditTableForm,
	manageResourceIds,
} from "../../../../helpers/appHelpers";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import { EmployeeDto } from "../../../../models/employee.dto";
import {
	useCreateLoanBenefit,
	useUpdateLoanBenefit,
} from "../../../../services/loan-benefit.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import Label from "../../../../components/form_fields/Label";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../routes/routes.paths";
import RateComponent from "../../../../components/form_fields/RateComponent";
import APISelectField from "../../../../components/form_fields/APISelectField";
import { LoanBenefitGroupDto } from "../../../../models/loan-benefit-group.dto";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	centralBankInterestRate: number;
	employerInterestRate: number;
	loanTenor: number;
	floor?: number;
	ceiling?: number;
	groupId?: number;
	employeeIds?: number[];
	employeeBandIds?: number[];
}

type ILoanBenefitModalForm = IAddEditTableForm & {
	employees: EmployeeDto[];
	employeeBands: EmployeeBandDto[];
	loanBenefitGroups: LoanBenefitGroupDto[];
	reloadTableData?: () => void;
};

const LoanBenefitModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	employees,
	employeeBands,
	loanBenefitGroups,
	reloadTableData,
}: ILoanBenefitModalForm) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const isGrouped = isEditForm && !!formDataValues?.groupId;
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			centralBankInterestRate: isEditForm
				? ((Number(formDataValues?.centralBankInterestRate) * 100) as any)
				: "",
			employerInterestRate: isEditForm
				? ((Number(formDataValues?.employerInterestRate) * 100) as any)
				: "",
			loanTenor: isEditForm ? formDataValues?.loanTenor : "",
			floor: isEditForm ? formDataValues?.floor : undefined,
			ceiling: isEditForm ? formDataValues?.ceiling : undefined,
			groupId: isEditForm ? formDataValues?.groupId : undefined,
		},
	});

	const [employeeIds, setEmployeeIds] = useState<number[]>([]);
	const [employeeBandIds, setEmployeeBandIds] = useState<number[]>([]);
	const { mutate: createLoanBenefit } = useCreateLoanBenefit();
	const { mutate: updateLoanBenefit } = useUpdateLoanBenefit();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);
		formatRate(data, "centralBankInterestRate");
		formatRate(data, "employerInterestRate");

		if (isEditForm) {
			const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
				manageResourceIds({
					newData: employeeIds,
					oldData:
						formDataValues?.employeeLoanBenefits?.map(
							(employeeLoanBenefit) => employeeLoanBenefit?.employee.id,
						) || [],
				});
			const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
				manageResourceIds({
					newData: employeeBandIds,
					oldData:
						formDataValues?.employeeBandLoanBenefits?.map(
							(employeeBandLoanBenefit) =>
								employeeBandLoanBenefit?.employeeBand.id,
						) || [],
				});

			data.addEmployeeIds = addEmployeeIds;
			data.removeEmployeeIds = removeEmployeeIds;
			data.addEmployeeBandIds = addEmployeeBandIds;
			data.removeEmployeeBandIds = removeEmployeeBandIds;

			updateLoanBenefit(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			data.employeeIds = employeeIds.length ? (employeeIds as any) : undefined;
			data.employeeBandIds = employeeBandIds.length
				? (employeeBandIds as any)
				: undefined;

			createLoanBenefit(
				{ payload: { companyId: currentCompany?.id, ...data } },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6">
					{isGrouped && (
						<p className="col-span-2 text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2">
							Some fields are managed by the loan benefit group and cannot be
							edited here.
						</p>
					)}
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							autoFocus
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							disabled={isGrouped}
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
								isGrouped ? "opacity-50 cursor-not-allowed" : ""
							} ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								disabled: isGrouped,
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="col-span-2 w-full">
						<Label title="Description" for="description" optional />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								disabled: isGrouped,
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
							required={false}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					{isEditForm && (
						<div className="w-full col-span-2">
							<Label title="Loan Benefit Group" for="groupId" optional />
							<APISelectField
								key="groupId"
								attributes={{
									id: "groupId",
									defaultValue: isEditForm
										? formDataValues?.groupId
										: undefined,
									disabled: isGrouped,
								}}
								placeholder="Select Loan Benefit Group"
								register={register}
								name="groupId"
								options={loanBenefitGroups}
								pathname={PATHS.SUB_COMPANY_SETTINGS.LOAN_BENEFIT_GROUPS}
								required={false}
								error={errors.groupId}
								valueAsNumber
							/>
						</div>
					)}
					<div className="w-full">
						<Label
							title="Central Bank Interest Rate"
							for="centralBankInterestRate"
						/>
						<RateComponent
							attributes={{
								id: "centralBankInterestRate",
								disabled: isGrouped,
							}}
							register={register}
							name="centralBankInterestRate"
							required={
								isGrouped ? false : "Enter central bank interest rate here"
							}
							error={errors.centralBankInterestRate}
						/>
						{errors.centralBankInterestRate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.centralBankInterestRate.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Employer Interest Rate" for="employerInterestRate" />
						<RateComponent
							attributes={{
								id: "employerInterestRate",
								disabled: isGrouped,
							}}
							register={register}
							name="employerInterestRate"
							required={isGrouped ? false : "Enter employer interest rate here"}
							error={errors.employerInterestRate}
						/>
						{errors.employerInterestRate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.employerInterestRate.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Floor" for="floor" optional />
						<MoneyInput
							attributes={{
								id: "floor",
								placeholder: "0",
								disabled: isGrouped,
							}}
							register={register}
							name="floor"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.floor : undefined}
							required={false}
							error={errors.floor}
						/>
						{errors.floor && (
							<p className="text-red-500 text-sm mt-1">
								{errors.floor.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Ceiling" for="ceiling" optional />
						<MoneyInput
							attributes={{
								id: "ceiling",
								placeholder: "0",
								disabled: isGrouped,
							}}
							register={register}
							name="ceiling"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.ceiling : undefined}
							required={false}
							error={errors.ceiling}
						/>
						{errors.ceiling && (
							<p className="text-red-500 text-sm mt-1">
								{errors.ceiling.message}
							</p>
						)}
					</div>
					<div className="flex flex-col w-full">
						<Label title="Loan Tenor" for="loanTenor" />
						<div className="flex items-center mt-2 h-14">
							<input
								placeholder="Enter value"
								type="number"
								{...register("loanTenor", {
									required: isGrouped ? false : "Enter loan tenor here",
									valueAsNumber: true,
								})}
								id="loanTenor"
								disabled={isGrouped}
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.loanTenor
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							/>
							<div className="h-full w-36 text-sm bg-black rounded-r-lg text-white flex items-center justify-center">
								Months
							</div>
						</div>
						{errors.loanTenor && (
							<p className="text-red-500 text-sm mt-1">
								{errors.loanTenor.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Assign Employees" for="employeeIds" optional />
						<MultiSelectField
							key={"employeeIds"}
							options={employees}
							setResourceIds={setEmployeeIds}
							placeholder={"Select Employees"}
							pathname={PATHS.EMPLOYEES}
							required={false}
							mapTexts={["firstName", "lastName"]}
							disabled={isGrouped}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeLoanBenefits?.map(
											(employeeLoanBenefit) => employeeLoanBenefit?.employee.id,
										) || []
							}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Assign Employee Bands"
							for="employeeBandIds"
							optional
						/>
						<MultiSelectField
							key={"employeeBandIds"}
							options={employeeBands}
							setResourceIds={setEmployeeBandIds}
							placeholder={"Select Bands"}
							pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
							required={false}
							defaultValues={
								!isEditForm
									? []
									: formDataValues?.employeeBandLoanBenefits?.map(
											(employeeBandLoanBenefit) =>
												employeeBandLoanBenefit?.employeeBand.id,
										) || []
							}
						/>
					</div>
				</div>
				<div className="w-full flex justify-end mt-12">
					<OrangeButton
						type="submit"
						title={`${
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Loan Benefit"
									: "Create Loan Benefit"
						}`}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default LoanBenefitModalForm;
